import React from 'react';
import { useQuery } from '@apollo/react-hooks';

import Collapsible from '../../library/collapsible/collapsible';
import EfficiencyDataInfo from '../efficiency-data-info/efficiency-data-info';
import RideData from '../ride-data-info/ride-data-info';
import StoppingDistanceInfo from '../stopping-distance-info/stopping-distance-info';
import TireLifeCostInfo from '../tire-life-cost-info/tire-life-cost-info';

import { GET_TREADWELL_PAGE } from '../../shared/graphql/treadwell-page-queries';

import { useFitment } from '../../shared/hooks/hooks';

import { isArrayWithLength } from '../../shared/utils/validators/validators';

import {
  FuelTypeEnum,
  ProductData,
  WeatherResult
} from '../../shared/types/graphql-types';

import './pdp-treadwell-section.scss';

interface PdpTreadwellSectionProps {
  isTreadwellFlow: boolean;
  product: ProductData;
  weatherDetails?: WeatherResult | null;
}

const PdpTreadwellSection = React.forwardRef<
  HTMLDivElement,
  PdpTreadwellSectionProps
>(function PdpTreadwellSection(props, ref) {
  const { isTreadwellFlow, product, weatherDetails } = props;
  const { selectedFitment } = useFitment();
  const { data, loading, error } = useQuery(GET_TREADWELL_PAGE, {
    variables: {
      id: 'treadwell',
      siteId: 'discounttire'
    }
  });

  const contentSlot = loading || error ? [] : data?.cms?.page?.contentSlots;

  const cmsData = contentSlot?.filter(data => data.position === 'Section4B');

  let rideContent, tlcContent, sdContent, fuelEfficiencyContent, evRangeContent;
  if (isArrayWithLength(cmsData)) {
    [
      rideContent,
      tlcContent,
      sdContent,
      fuelEfficiencyContent,
      evRangeContent
    ] = cmsData?.[0]?.cmsComponents || {};
  }

  let isBatteryElectric = selectedFitment?.fuelType === FuelTypeEnum.Bev;

  const efficiencyContent = isBatteryElectric
    ? evRangeContent
    : fuelEfficiencyContent;

  const { brand: currentProductBrand, name: currentProductName } =
    product || {};
  const { rank, rideRating, stopping } = product?.recommendationData || {};
  const { dryDistanceProduct, wetDistanceProduct } =
    product?.recommendedDistanceData || {};
  const {
    brand: recommendedDryProductBrand,
    name: recommendedDryProductName,
    url: recommendedDryProductUrl,
    recommendationData: recommendedDryData
  } = dryDistanceProduct || {};
  const { rank: recommendedDryRank, stopping: recommendedDryStopping } =
    recommendedDryData || {};
  const { dryDistance: recommendedDryDistance } = recommendedDryStopping || {};
  const {
    brand: recommendedWetProductBrand,
    name: recommendedWetProductName,
    url: recommendedWetProductUrl,
    recommendationData: recommendedWetData
  } = wetDistanceProduct || {};
  const { rank: recommendedWetRank, stopping: recommendedWetStopping } =
    recommendedWetData || {};
  const {
    wetDistance: recommendedWetDistance,
    wetWornDistance: recommendedWetWornDistance
  } = recommendedWetStopping || {};

  const {
    dryDistance,
    dryRating,
    rollingResistanceRating,
    wetDistance,
    wetWornDistance,
    wetRating,
    winterRating
  } = stopping || {};
  const { handling, wetHandling, quiet, comfort } = rideRating || {};

  return (
    <>
      <div
        className="pitpass-treadwell-section__logo-container"
        ref={ref}
        styleName="logo-container"
      >
        <div styleName="logo" />
        <h5>Data-tested results</h5>
      </div>
      <div styleName="container">
        <h3 styleName="heading">STOPPING DISTANCE</h3>
        <StoppingDistanceInfo
          currentProductLabel={`${currentProductBrand} ${currentProductName}`}
          dryDistance={dryDistance}
          dryRating={dryRating}
          hideRecommended={!isTreadwellFlow}
          isLT={['SL', 'XL', 'HL'].indexOf(product?.loadRange || '') === -1}
          precipitationDaysPerYear={weatherDetails?.precipitationDaysPerYear}
          rank={rank}
          recommendedDryDistance={recommendedDryDistance}
          recommendedDryProductLabel={`${recommendedDryProductBrand} ${recommendedDryProductName}`}
          recommendedDryProductUrl={recommendedDryProductUrl}
          recommendedDryRank={recommendedDryRank}
          recommendedWetDistance={recommendedWetDistance}
          recommendedWetProductLabel={`${recommendedWetProductBrand} ${recommendedWetProductName}`}
          recommendedWetProductUrl={recommendedWetProductUrl}
          recommendedWetRank={recommendedWetRank}
          recommendedWetWornDistance={recommendedWetWornDistance}
          wetDistance={wetDistance}
          wetRating={wetRating}
          wetWornDistance={wetWornDistance}
          winterDaysPerYear={weatherDetails?.winterDaysPerYear}
          winterRating={winterRating}
        />
        <Collapsible
          label="Stopping Distance Data Info"
          styleName="treadwell-pdp-data"
        >
          <div
            dangerouslySetInnerHTML={{
              __html: sdContent?.content?.[0]?.text
            }}
            styleName="content"
          />
        </Collapsible>

        <div styleName="ride-tlc-row">
          <div styleName="ride-wrapper">
            <div>
              <h3 styleName="heading">
                {isBatteryElectric ? 'EV RANGE' : 'EFFICIENCY'}
              </h3>
              <EfficiencyDataInfo
                className="pdp-treadwell-efficiency-data"
                isEV={isBatteryElectric}
                value={rollingResistanceRating}
              />
              <Collapsible
                label={
                  isBatteryElectric
                    ? 'EV Range Data Info'
                    : 'Efficiency Data Info'
                }
                styleName="treadwell-pdp-data"
              >
                <div
                  dangerouslySetInnerHTML={{
                    __html: efficiencyContent?.content?.[0]?.text
                  }}
                  styleName="content"
                />
              </Collapsible>
            </div>
            <div>
              <h3 styleName="heading">RIDE QUALITY</h3>
              <RideData
                className="pdp-treadwell-ride-data"
                comfort={comfort}
                handling={handling}
                quiet={quiet}
                wetHandling={wetHandling}
              />
              <Collapsible
                className="hide-lg"
                label="Ride Data Info"
                styleName="treadwell-pdp-data"
              >
                <div
                  dangerouslySetInnerHTML={{
                    __html: rideContent?.content?.[0]?.text
                  }}
                  styleName="content"
                />
              </Collapsible>
            </div>
          </div>

          <div styleName="tlc-wrapper">
            <h3 styleName="heading">TIRE LIFE & COST</h3>
            <TireLifeCostInfo max={55} min={5} product={product} showTooltip />
            <Collapsible
              className="hide-lg"
              label="Tire Life & Cost Data Info"
              styleName="treadwell-pdp-data"
            >
              <div
                dangerouslySetInnerHTML={{
                  __html: tlcContent?.content?.[0]?.text
                }}
                styleName="content"
              />
            </Collapsible>
          </div>
        </div>
        <div className="display-flex-lg" styleName="ride-tlc-row">
          <div styleName="ride-wrapper">
            <Collapsible label="Ride Data Info" styleName="treadwell-pdp-data">
              <div
                dangerouslySetInnerHTML={{
                  __html: rideContent?.content?.[0]?.text
                }}
                styleName="content"
              />
            </Collapsible>
          </div>
          <div styleName="tlc-wrapper">
            <Collapsible
              label="Tire Life & Cost Data Info"
              styleName="treadwell-pdp-data"
            >
              <div
                dangerouslySetInnerHTML={{
                  __html: tlcContent?.content?.[0]?.text
                }}
                styleName="content"
              />
            </Collapsible>
          </div>
        </div>
      </div>
    </>
  );
});

export default PdpTreadwellSection;
